import { TFunction } from 'i18next';
import { ComponentType } from 'react';
import z from 'zod';
import { FeatureFlags } from '@provider-directory/types/flags';
import { LocationResource } from '@provider-directory/types/locations.contracts';
import { ProviderResource } from '@provider-directory/types/providers.contracts';
import {
  getPrimaryLocation,
  getProviderPrimarySpecialty,
  isPrimaryCarePediatricsProvider,
} from './provider.model';

export const DECISION_TREE_INITIAL_STEP: DecisionTreeSceneSlug = 'is-adult';

export const SpecialtyBranch = z.enum([
  'pediatrics',
  'cardiology',
  'dermatology',
  'allergy',
  'orthopedics',
  'endocrinology',
  'gastroenterology',
]);

export type SpecialtyBranch = z.infer<typeof SpecialtyBranch>;

const EXCLUDED_SCENE_PATTERNS = ['error', 'disclaimer'];

export const isExcluded = (slug?: string | null): boolean => {
  if (!slug) return true;
  const s = String(slug);
  return EXCLUDED_SCENE_PATTERNS.some((pattern) => s.includes(pattern));
};

/**
 * Checks whether an answer exists and is non-empty for a given scene slug.
 * Empty string and undefined are considered "no answer".
 */
export const hasAnswer = (
  slug: string,
  answers: Record<string, unknown>,
): boolean =>
  Object.prototype.hasOwnProperty.call(answers, slug) &&
  answers[slug] !== undefined &&
  answers[slug] !== '';

/**
 * Walks the history from the end and returns the last *unanswered* scene slug.
 * Returns null if all scenes in the history have answers.
 */
export const findLastUnanswered = (
  history: string[],
  answers: Record<string, unknown>,
): string | null => {
  for (let i = history.length - 1; i >= 0; i--) {
    const slug = history[i];
    if (!hasAnswer(slug, answers)) return slug;
  }
  return null;
};

/**
 * Safe helper to get the last element of an array (or null if empty).
 */
export const lastOf = <T>(arr: T[]): T | null =>
  arr.length > 0 ? arr[arr.length - 1] : null;

export const getDecisionTreeSpecialty = ({
  specialtyMap,
  provider,
  featureFlags,
}: {
  specialtyMap: Record<string, SpecialtyBranch>;
  provider?: ProviderResource | null;
  featureFlags: FeatureFlags;
}): SpecialtyBranch | null => {
  if (!provider) return null;

  const primarySpecialty =
    getProviderPrimarySpecialty(provider)?.name.toLowerCase() ?? '';

  if (
    specialtyMap[primarySpecialty] === 'cardiology' &&
    featureFlags.isCardiologyDecisionTreeEnabled
  ) {
    return 'cardiology';
  }

  if (
    specialtyMap[primarySpecialty] === 'dermatology' &&
    featureFlags.isDermatologyDecisionTreeEnabled
  ) {
    return 'dermatology';
  }

  if (
    specialtyMap[primarySpecialty] === 'allergy' &&
    featureFlags.isAllergyDecisionTreeEnabled
  ) {
    return 'allergy';
  }

  if (
    specialtyMap[primarySpecialty] === 'orthopedics' &&
    featureFlags.isOrthoDecisionTreeEnabled
  ) {
    return 'orthopedics';
  }

  if (
    specialtyMap[primarySpecialty] === 'endocrinology' &&
    featureFlags.isEndocrinologyDecisionTreeEnabled
  ) {
    return 'endocrinology';
  }

  if (
    specialtyMap[primarySpecialty] === 'gastroenterology' &&
    featureFlags.isGastroenterologyDecisionTreeEnabled
  ) {
    return 'gastroenterology';
  }

  if (
    featureFlags.isPrimaryCareTreeEnabled &&
    isPrimaryCarePediatricsProvider(provider, specialtyMap)
  ) {
    return 'pediatrics';
  }

  return null;
};

const DecisionTreeQuestionSceneSlug = z.enum([
  'is-adult',
  'seen-cardiology-recently',
  'seen-dermatology-recently',
  'seen-allergy-recently',
  'seen-orthopedics-recently',
  'seen-endocrinology-recently',
  'seen-gastroenterology-recently',
  'ortho-workers-comp',
  'reason-for-visit',
  'ortho-neck-spine-back',
  'ortho-shoulder',
  'ortho-arm-elbow-hand-wrist',
  'ortho-hip',
  'ortho-knee',
  'ortho-foot-ankle',
  'ortho-sports-medicine',
  'ortho-achilles',
  'ortho-reason-for-visit',
  'reason-for-visit-medicaid',
  'reason-for-visit-ortho-foot-care-medicaid',
  'endocrinology-reason-for-visit',
  'endocrinology-diabetes-type',
  'endocrinology-thyroid-type',
  'endocrinology-pregnancy-question',
  'endocrinology-something-else',
  'gastroenterology-reason-for-visit',
  // Primary Care with Pediatrics
  'pediatrics-seen-recently',
  'pediatrics-reason-for-visit-pediatric',
  'pediatrics-reason-for-visit-adult',
]);
export type DecisionTreeQuestionSceneSlug = z.infer<
  typeof DecisionTreeQuestionSceneSlug
>;
const DecisionTreeFormSceneSlug = z.enum(['select-time-modal']);
export type DecisionTreeFormSceneSlug = z.infer<
  typeof DecisionTreeFormSceneSlug
>;
const DecisionTreeErrorSceneSlug = z.enum([
  'is-adult-cardiology-error',
  'is-adult-dermatology-error',
  'is-adult-allergy-error',
  'is-adult-ortho-error',
  'is-adult-endocrinology-error',
  'seen-dermatology-recently-error',
  'seen-cardiology-recently-error',
  'seen-allergy-recently-error',
  'seen-ortho-recently-error',
  'seen-endocrinology-recently-error',
  'seen-gastroenterology-recently-error',
  'ortho-workers-comp-error',
  'reason-for-visit-error',
  'reason-for-visit-provider-error',
  'foot-care-podiatry-error',
  'endocrinology-pregnancy-error',
  'endocrinology-fertility-error',
  'endocrinology-weight-management-check-error',
  'is-adult-gastroenterology-error',
  'gastroenterology-urgent-error',
  // Primary Care with Pediatrics
  'pediatrics-newborn-error',
  'pediatrics-established-pediatric-error',
  'pediatrics-established-adult-error',
]);
export type DecisionTreeErrorSceneSlug = z.infer<
  typeof DecisionTreeErrorSceneSlug
>;

export const AppointmentRequestSceneSlug = z.enum([
  'select-time',
  'patient-info',
  'insurance',
  'review',
  'confirm',
  'scheduling-error',
  'appointment-error',
]);
export type AppointmentRequestSceneSlug = z.infer<
  typeof AppointmentRequestSceneSlug
>;

const DecisionTreeDisclaimerSceneSlug = z.enum(['disclaimer']);
export type DecisionTreeDisclaimerSceneSlug = z.infer<
  typeof DecisionTreeDisclaimerSceneSlug
>;

// TODO: Remove hardcoded slugs when decision tree config is coming from api
const DecisionTreeSceneSlug = z.enum([
  ...DecisionTreeQuestionSceneSlug.options,
  ...DecisionTreeFormSceneSlug.options,
  ...DecisionTreeErrorSceneSlug.options,
  ...DecisionTreeDisclaimerSceneSlug.options,
]);

export type DecisionTreeSceneSlug = z.infer<typeof DecisionTreeSceneSlug>;

const SceneSlug = z.union([AppointmentRequestSceneSlug, DecisionTreeSceneSlug]);
export type SceneSlug = z.infer<typeof SceneSlug>;

export interface Scene {
  component: ComponentType<any>;
  sceneSlug: SceneSlug;
}

export interface SceneValidation {
  type:
    | 'rfvSupportedByProvider'
    | 'rfvAllergyDisclaimer'
    | 'weightManagementHospitalCheck'
    | 'pcpPediatricsEstablishedPatientRouting'
    | 'pcpPediatricsNewPatientRouting';
  failedValidationScene: SceneSlug;
}

export type SceneAction = 'setRfv' | 'rfvAddAgeGroup' | 'rfvAppendValue';

export interface DecisionTreeQuestionOption {
  value: string;
  label: string;
  subCopy?: string;
  /**
   * HTML content for tooltip with translatable placeholders.
   * Use {{translationKey}} syntax for translatable text within HTML.
   * Example: "<p>{{decisionTree.tooltip.title}}</p><ul><li>{{decisionTree.tooltip.item1}}</li></ul>"
   */
  tooltip?: string;
  /** Translation key for the tooltip's aria-label */
  tooltipAriaLabel?: string;
  nextScene: SceneSlug;
  validation?: SceneValidation | SceneValidation[];
  action?: SceneAction;
}

export interface DecisionTreeQuestionSceneProps {
  progress: number;
  name: DecisionTreeQuestionSceneSlug;
  text: string;
  specialty?: string;
  warning?: string;
  prevScene: SceneSlug | null;
  options: DecisionTreeQuestionOption[];
  action?: SceneAction;
}

export enum DecisionTreeMode {
  Normal = 'normal',
  RedirectOnRfvSelect = 'redirect-on-rfv-select',
}

const isAppointmentSceneSlug = (value: string): boolean => {
  const result = AppointmentRequestSceneSlug.safeParse(value);
  return result.success;
};

export const getNavigationOptions = (
  scene: SceneSlug,
  rfvId?: string,
  mode?: DecisionTreeMode,
):
  | {
      redirectLink?: string;
    }
  | undefined => {
  if (isAppointmentSceneSlug(scene)) {
    return {
      redirectLink: `/providers/appointment/request/${scene}`,
    };
  } else if (
    mode === DecisionTreeMode.RedirectOnRfvSelect &&
    rfvId &&
    scene === DecisionTreeFormSceneSlug.enum['select-time-modal']
  ) {
    return {
      redirectLink: `/providers/find?reason-for-visit=${rfvId}`,
    };
  }
};

/**
 * Gets the location for decision tree analytics events.
 * Uses the location from appointment.departmentId (when decision tree opened),
 * with fallbacks to primary location or first location.
 *
 * @param provider - The provider resource
 * @param departmentId - The EHR location ID from appointment context (when decision tree opened)
 * @returns The location resource, or undefined if provider has no locations
 */
export const getDecisionTreeLocation = (
  provider?: Partial<ProviderResource> | null,
  departmentId?: string | null,
): LocationResource | undefined => {
  if (!provider || !provider.locations || provider.locations.length === 0) {
    return undefined;
  }

  // First, try to find location by departmentId (the one selected when decision tree opened)
  if (departmentId) {
    const locationByDepartmentId = provider.locations.find(
      (loc) => loc.ehrExternalId === departmentId,
    );
    if (locationByDepartmentId) {
      return locationByDepartmentId;
    }
  }

  // Fallback to primary location, then first location
  return getPrimaryLocation(provider.locations);
};

/**
 * Parses tooltip content and replaces {{translationKey}} placeholders with translated values.
 * This allows tooltips in sceneFlows to contain translatable HTML content.
 *
 * @example
 * Input: "<p>{{decisionTree.tooltip.title}}</p><li>{{decisionTree.tooltip.item1}}</li>"
 * Output: "<p>Choose this for:</p><li>Heartburn or reflux</li>"
 */
export const parseTooltipContent = (tooltip: string, t: TFunction): string =>
  tooltip.replace(/\{\{([^}]+)\}\}/g, (_, key: string) =>
    t(key.trim() as Parameters<typeof t>[0]),
  );

export const isValueAllowed = (
  options: DecisionTreeQuestionSceneProps['options'],
  val?: string,
): boolean => options.some((o) => o.value === val);
